// ============================================================================
// diagScroll — Diagnostico EN VIVO de saltos de scroll (para el bug de la ficha
// de producto en movil). Se enciende/apaga tocando 5 veces la version en el pie
// del menu de perfil. Cuando esta APAGADO no hace absolutamente nada (no registra
// listeners, no pinta panel) -> cero impacto en el uso normal.
//
// Que captura por cada SALTO de scroll (delta grande no explicado por arrastre):
//  - de/a/delta de posicion + hora
//  - ultimo elemento TOCADO antes del salto (tag, clase, texto corto, si es
//    boton/enlace/tiene onclick, si esta dentro de la ficha)
//  - si coincidio con: focus de input, carga de imagen que empuja el layout,
//    resize (barra del navegador), re-render (mutacion grande del DOM),
//    scroll-snap CSS.
//  - un VEREDICTO legible de la causa mas probable.
// El boton "Resumen" pinta los ultimos 10 saltos en una capa solida y legible
// para tomar UNA captura.
// ============================================================================

import { storage, STORAGE_KEYS } from './storage';

const UMBRAL_SALTO = 150;      // px: delta minimo para considerarlo "salto"
const VENTANA = 500;           // ms: ventana para correlacionar eventos con el salto
const MUTACION_GRANDE = 12;    // nodos agregados/quitados para considerar "re-render"

let activo = false;
let estado = null; // objeto con listeners, panel, buffers

function ahora() {
  // Date.now esta permitido en el navegador (no en workflows). Aqui es cliente.
  try { return Date.now(); } catch { return 0; }
}

export function isDiagOn() {
  try { return storage.getRaw(STORAGE_KEYS.DIAG_SCROLL) === '1'; } catch { return false; }
}

// Escapa texto derivado del DOM antes de meterlo en el HTML del panel (evita XSS:
// el texto del elemento tocado podria contener HTML, p.ej. una nota con <img onerror=...>).
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

// Llamar una vez al arrancar la app: si el flag esta encendido, activa el diag.
export function initDiagScroll() {
  try { if (isDiagOn()) start(); } catch { /* nunca romper la app */ }
}

// Alterna encendido/apagado (lo usa el pie del menu con 5 toques). Devuelve el
// nuevo estado (true = encendido).
export function toggleDiagScroll() {
  try {
    if (activo) { stop(); storage.set(STORAGE_KEYS.DIAG_SCROLL, '0'); return false; }
    storage.set(STORAGE_KEYS.DIAG_SCROLL, '1'); start(); return true;
  } catch { return activo; }
}

function textoCorto(el) {
  try {
    const t = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.slice(0, 40);
  } catch { return ''; }
}

function describeEl(el) {
  if (!el || el === document || el === window) return { tag: '(doc)', cls: '', text: '' };
  const tag = (el.tagName || '?').toLowerCase();
  const cls = (typeof el.className === 'string' ? el.className : '').slice(0, 40);
  const enlace = !!(el.closest && el.closest('a'));
  const boton = tag === 'button' || !!(el.closest && el.closest('button'));
  let onclick = false, cursorPointer = false, dentroFicha = false;
  try { onclick = !!el.onclick || !!(el.closest && el.closest('[onclick]')); } catch { /* noop */ }
  try { cursorPointer = getComputedStyle(el).cursor === 'pointer'; } catch { /* noop */ }
  try { dentroFicha = !!(el.closest && el.closest('.pp-grid, [data-ficha], main')); } catch { /* noop */ }
  return { tag, cls, text: textoCorto(el), enlace, boton, onclick, cursorPointer, dentroFicha };
}

function scrollSnapPresente() {
  try {
    const cont = [document.documentElement, document.body, ...document.querySelectorAll('*')];
    for (const el of cont.slice(0, 400)) {
      const st = getComputedStyle(el).scrollSnapType;
      if (st && st !== 'none') return true;
    }
  } catch { /* noop */ }
  return false;
}

function start() {
  if (activo) return;
  activo = true;
  const s = {
    lastY: window.scrollY || window.pageYOffset || 0,
    lastTapT: 0, lastTap: null,
    lastMoveT: 0, dedoAbajo: false,
    lastFocusT: 0, lastFocus: null,
    lastImgT: 0, lastImgArriba: false,
    lastResizeT: 0,
    lastMutT: 0, lastMutBig: false,
    saltos: [],           // historial de saltos
    snap: scrollSnapPresente(),
    handlers: [],
    obs: null,
    panel: null,
  };
  estado = s;

  const on = (target, tipo, fn, opts) => {
    target.addEventListener(tipo, fn, opts);
    s.handlers.push(() => target.removeEventListener(tipo, fn, opts));
  };

  // Toques
  on(window, 'touchstart', (e) => { s.dedoAbajo = true; s.lastTapT = ahora(); s.lastTap = describeEl(e.target); }, { passive: true, capture: true });
  on(window, 'pointerdown', (e) => { s.lastTapT = ahora(); s.lastTap = describeEl(e.target); }, { passive: true, capture: true });
  on(window, 'touchmove', () => { s.lastMoveT = ahora(); }, { passive: true, capture: true });
  on(window, 'touchend', () => { s.dedoAbajo = false; }, { passive: true, capture: true });
  on(window, 'click', (e) => { s.lastTapT = ahora(); s.lastTap = describeEl(e.target); }, { passive: true, capture: true });

  // Focus (posible zoom/scroll de input)
  on(window, 'focusin', (e) => { s.lastFocusT = ahora(); s.lastFocus = describeEl(e.target); }, { passive: true, capture: true });

  // Resize (barra del navegador / teclado)
  on(window, 'resize', () => { s.lastResizeT = ahora(); }, { passive: true });

  // Carga de imagenes (empuja layout si esta por ENCIMA del viewport)
  on(document, 'load', (e) => {
    const el = e.target;
    if (el && el.tagName === 'IMG') {
      s.lastImgT = ahora();
      try { s.lastImgArriba = el.getBoundingClientRect().top < 0; } catch { s.lastImgArriba = false; }
    }
  }, { capture: true });

  // Re-render / cambio de contenido (mutacion grande del DOM)
  try {
    s.obs = new MutationObserver((muts) => {
      let n = 0;
      for (const m of muts) n += (m.addedNodes ? m.addedNodes.length : 0) + (m.removedNodes ? m.removedNodes.length : 0);
      if (n >= 1) { s.lastMutT = ahora(); s.lastMutBig = n >= MUTACION_GRANDE; }
    });
    s.obs.observe(document.body, { childList: true, subtree: true });
  } catch { /* noop */ }

  // Detector de SALTO
  on(window, 'scroll', () => {
    const y = window.scrollY || window.pageYOffset || 0;
    const delta = y - s.lastY;
    const adelta = Math.abs(delta);
    s.lastY = y;
    if (adelta < UMBRAL_SALTO) return;
    const t = ahora();
    // Arrastre normal del dedo: dedo abajo y con movimiento muy reciente.
    const arrastre = s.dedoAbajo && (t - s.lastMoveT) < 90;
    if (arrastre) return;
    registrarSalto(s, { t, from: Math.round(y - delta), to: Math.round(y), delta: Math.round(delta) });
  }, { passive: true });

  pintarPanel(s);
}

function coincide(t, cuando) { return cuando > 0 && (t - cuando) >= 0 && (t - cuando) <= VENTANA; }

function registrarSalto(s, base) {
  const t = base.t;
  const ev = {
    ...base,
    hora: new Date(t).toLocaleTimeString(),
    tap: coincide(t, s.lastTapT) ? s.lastTap : null,
    msTap: s.lastTapT ? (t - s.lastTapT) : -1,
    focus: coincide(t, s.lastFocusT) ? s.lastFocus : null,
    imgArriba: coincide(t, s.lastImgT) ? s.lastImgArriba : false,
    resize: coincide(t, s.lastResizeT),
    reRender: coincide(t, s.lastMutT) && s.lastMutBig,
    snap: s.snap,
    dedoAbajo: s.dedoAbajo,
  };
  ev.veredicto = veredictoDe(ev);
  s.saltos.push(ev);
  if (s.saltos.length > 40) s.saltos.shift();
  actualizarPill(s);
}

function veredictoDe(ev) {
  // Prioridad segun lo mas explicativo de un "salta a otra seccion al tocar".
  if (ev.tap && (ev.tap.enlace)) return 'TOQUE en ENLACE (ancla) -> navegacion interna';
  if (ev.focus && /input|textarea|select/.test(ev.focus.tag)) return 'FOCUS de input (zoom/scroll al enfocar)';
  if (ev.tap && (ev.tap.boton || ev.tap.onclick || ev.tap.cursorPointer)) return 'TOQUE en elemento con accion (boton/handler)';
  if (ev.imgArriba) return 'IMAGEN que carga ARRIBA y empuja el layout';
  if (ev.resize) return 'RESIZE del viewport (barra del navegador/teclado)';
  if (ev.reRender) return 'RE-RENDER / cambio grande de contenido';
  if (ev.snap) return 'posible SCROLL-SNAP (CSS) — hay scroll-snap en la pagina';
  if (ev.tap) return 'TOQUE (elemento sin accion aparente) — revisar';
  return 'salto sin causa correlacionada (revisar contexto)';
}

// --------------------------- UI del panel ---------------------------

function pintarPanel(s) {
  const pill = document.createElement('div');
  pill.id = 'beep-diag-pill';
  // Esquina inferior IZQUIERDA y algo elevada: abajo-DERECHA viven el carrito flotante (ficha) y
  // el boton del Asistente (escritorio); el borde inferior lo puede ocupar la barra de navegacion
  // movil. left:12 + bottom:84 despeja los tres -> sin toques cruzados en la pantalla a depurar.
  pill.style.cssText = 'position:fixed;left:12px;bottom:84px;z-index:2147483647;background:#0b2440;color:#fff;font-family:monospace;font-size:13px;border-radius:24px;padding:6px 6px 6px 14px;box-shadow:0 3px 12px rgba(0,0,0,.4);display:flex;align-items:center;gap:10px;';
  pill.innerHTML = '<span id="beep-diag-count">saltos: 0</span>' +
    '<button id="beep-diag-resumen" style="background:#fff;color:#0b2440;border:none;border-radius:20px;min-height:44px;padding:6px 14px;font-weight:700;font-family:inherit;font-size:14px;cursor:pointer">Resumen</button>';
  document.body.appendChild(pill);
  s.panel = pill;
  document.getElementById('beep-diag-resumen').addEventListener('click', () => mostrarResumen(s));
}

function actualizarPill(s) {
  const c = document.getElementById('beep-diag-count');
  if (c) {
    const ult = s.saltos[s.saltos.length - 1];
    c.textContent = 'saltos: ' + s.saltos.length + (ult ? ' (' + ult.delta + 'px)' : '');
    c.style.color = '#ffd166';
  }
}

function mostrarResumen(s) {
  const prev = document.getElementById('beep-diag-resumen-cap');
  if (prev) prev.remove();
  const cap = document.createElement('div');
  cap.id = 'beep-diag-resumen-cap';
  cap.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:#0b2440;color:#fff;font-family:monospace;font-size:14px;line-height:1.5;overflow:auto;padding:14px;';
  const ultimos = s.saltos.slice(-10).reverse();
  const bundle = (function () {
    const sc = document.querySelector('script[src*="/static/js/main."]');
    return sc ? (sc.src.match(/main\.[a-f0-9]+\.js/) || ['?'])[0] : '?';
  })();
  // Todo lo derivado del DOM (bundle, tag, cls, text, veredicto) se pasa por esc() antes de
  // interpolar en el HTML del panel -> el contenido tocado NO puede ejecutar HTML/JS (anti-XSS).
  let html = '<div style="font-weight:700;font-size:14px;margin-bottom:6px">Beep · resumen de saltos de scroll</div>';
  html += '<div style="opacity:.8;margin-bottom:2px">Bundle: ' + esc(bundle) + '</div>';
  html += '<div style="opacity:.8;margin-bottom:10px">Total saltos: ' + s.saltos.length + ' · scroll-snap en pagina: ' + (s.snap ? 'SI' : 'no') + '</div>';
  if (!ultimos.length) {
    html += '<div style="background:#12345a;padding:10px;border-radius:8px">Aun no se detectaron saltos. Usa la ficha normal (baja tocando) y cuando brinque, vuelve a abrir este Resumen.</div>';
  } else {
    ultimos.forEach((e, i) => {
      const tapTxt = e.tap
        ? (esc(e.tap.tag) + (e.tap.cls ? '.' + esc(e.tap.cls) : '') + (e.tap.enlace ? ' [enlace]' : '') + (e.tap.boton ? ' [boton]' : '') + (e.tap.text ? ' "' + esc(e.tap.text) + '"' : '') + ' (' + e.msTap + 'ms antes)')
        : '—';
      html += '<div style="background:#12345a;border-radius:8px;padding:10px;margin-bottom:8px">' +
        '<div style="font-weight:700;color:#ffd166">#' + (ultimos.length - i) + ' · ' + esc(e.hora) + ' · ' + e.from + '→' + e.to + ' (' + (e.delta > 0 ? '+' : '') + e.delta + 'px)</div>' +
        '<div style="margin-top:3px">Causa probable: <b>' + esc(e.veredicto) + '</b></div>' +
        '<div style="opacity:.85;margin-top:3px">Toque: ' + tapTxt + '</div>' +
        '<div style="opacity:.85">Focus: ' + (e.focus ? esc(e.focus.tag) : '—') + ' · Img arriba: ' + (e.imgArriba ? 'SI' : 'no') + ' · Resize: ' + (e.resize ? 'SI' : 'no') + ' · Re-render: ' + (e.reRender ? 'SI' : 'no') + ' · dedo abajo: ' + (e.dedoAbajo ? 'SI' : 'no') + '</div>' +
        '</div>';
    });
  }
  html += '<div style="display:flex;gap:10px;margin-top:6px">' +
    '<button id="beep-diag-cerrar" style="flex:1;background:#fff;color:#0b2440;border:none;border-radius:8px;min-height:44px;font-weight:700;font-family:inherit;font-size:14px;cursor:pointer">Cerrar</button>' +
    '<button id="beep-diag-limpiar" style="flex:1;background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5);border-radius:8px;min-height:44px;font-family:inherit;font-size:14px;cursor:pointer">Limpiar</button>' +
    '</div>';
  cap.innerHTML = html;
  document.body.appendChild(cap);
  document.getElementById('beep-diag-cerrar').addEventListener('click', () => cap.remove());
  document.getElementById('beep-diag-limpiar').addEventListener('click', () => { s.saltos.length = 0; actualizarPill(s); cap.remove(); });
}

function stop() {
  activo = false;
  const s = estado;
  if (!s) return;
  try { s.handlers.forEach((off) => off()); } catch { /* noop */ }
  try { if (s.obs) s.obs.disconnect(); } catch { /* noop */ }
  try { if (s.panel) s.panel.remove(); } catch { /* noop */ }
  try { const cap = document.getElementById('beep-diag-resumen-cap'); if (cap) cap.remove(); } catch { /* noop */ }
  estado = null;
}
